import React, { useState, useEffect } from 'react';
import {
  HiOutlinePlus,
  HiOutlineMagnifyingGlass,
  HiOutlinePuzzlePiece,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineXMark
} from 'react-icons/hi2';
import { authService } from '../../services';
import masterDataService from '../../services/masterDataService';
import SharedModal from '../../components/common/SharedModal';
import PageLoading from '../../components/common/PageLoading';

const RootCauseCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    categoryCode: '',
    categoryName: '',
    description: '',
    status: 'Active',
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const tenantId = authService.getTenantId();

  useEffect(() => {
    if (tenantId) {
      loadCategories();
    } else {
      setError('Please login to view root cause categories');
      setLoading(false);
    }
  }, [tenantId]);

  const loadCategories = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await masterDataService.getRootCauseCategories(tenantId);
      setCategories(data);
    } catch (err) {
      setError('Failed to load root cause categories. Please try again.');
      console.error('Error loading root cause categories:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredCategories = categories.filter((cat) =>
    cat.categoryName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cat.categoryCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cat.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAdd = () => {
    setEditingCategory(null);
    setFormData({
      categoryCode: '',
      categoryName: '',
      description: '',
      status: 'Active',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      categoryCode: category.categoryCode || '',
      categoryName: category.categoryName || '',
      description: category.description || '',
      status: category.status || 'Active',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    try {
      setError('');
      await masterDataService.deleteRootCauseCategory(id);
      await loadCategories();
      setDeleteConfirm(null);
    } catch (err) {
      setError('Failed to delete root cause category. Please try again.');
      console.error('Error deleting root cause category:', err);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.categoryCode.trim()) {
      errors.categoryCode = 'Category code is required';
    } else if (formData.categoryCode.length > 50) {
      errors.categoryCode = 'Category code must be less than 50 characters';
    }
    if (!formData.categoryName.trim()) {
      errors.categoryName = 'Category name is required';
    } else if (formData.categoryName.length > 100) {
      errors.categoryName = 'Category name must be less than 100 characters';
    }
    if (formData.description && formData.description.length > 500) {
      errors.description = 'Description must be less than 500 characters';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const categoryData = {
        tenantId: tenantId,
        categoryCode: formData.categoryCode.trim(),
        categoryName: formData.categoryName.trim(),
        description: formData.description.trim() || null,
        status: formData.status,
      };

      if (editingCategory) {
        categoryData.rootCauseCategoryId = editingCategory.rootCauseCategoryId;
        await masterDataService.updateRootCauseCategory(editingCategory.rootCauseCategoryId, categoryData);
      } else {
        await masterDataService.createRootCauseCategory(categoryData);
      }

      await loadCategories();
      setShowModal(false);
      setFormData({ categoryCode: '', categoryName: '', description: '', status: 'Active' });
      setEditingCategory(null);
    } catch (err) {
      const errorMessage = err.response?.data?.message ||
        err.response?.data?.errors ||
        err.message ||
        'Failed to save root cause category. Please try again.';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingCategory(null);
    setFormData({ categoryCode: '', categoryName: '', description: '', status: 'Active' });
    setFormErrors({});
  };

  if (loading) {
    return <PageLoading message="Loading root cause categories..." />;
  }

  return (
    <div className="pagecontainer">
      <div className="page-header">
        <div className="header-content">
          <h1>Root Cause Categories</h1>
          <p>Manage categories for root cause analysis (e.g., Man, Machine, Method, Material, Environment, Measurement)</p>
        </div>
        <div className='header-actions'>
          <button className="btn-primary btn" onClick={handleAdd}>
            <HiOutlinePlus />
            Add Category
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="table-container pad10 kizantable ">
        <div className="table-header">
          <div className='left-group'>
            <div className="search-box">
              <HiOutlineMagnifyingGlass className="search-icon" />
              <input
                type="text"
                placeholder="Search categories..."
                value={searchTerm}
                onChange={handleSearch}
                className="search-input"
              />
            </div>
            <div className="table-info">
              {filteredCategories.length} {filteredCategories.length === 1 ? 'category' : 'categories'}
            </div>
          </div>
        </div>

        {filteredCategories.length === 0 ? (
          <div className="empty-state">
            <HiOutlinePuzzlePiece className="empty-icon" />
            <h3>No root cause categories found</h3>
            <p>
              {searchTerm
                ? 'Try adjusting your search terms'
                : 'Get started by creating your first root cause category'}
            </p>
            {!searchTerm && (
              <button className="btn-primary" onClick={handleAdd}>
                <HiOutlinePlus className="btn-icon" />
                New Category
              </button>
            )}
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Code</th>
                <th>Name</th>
                <th>Description</th>
                <th>Status</th>
                <th>Created</th>
                <th className='text-center'>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCategories.map((cat) => (
                <tr key={cat.rootCauseCategoryId}>
                  <td>{cat.rootCauseCategoryId}</td>
                  <td className="code-cell">{cat.categoryCode}</td>
                  <td className="name-cell">{cat.categoryName}</td>
                  <td className="description-cell">{cat.description || '-'}</td>
                  <td>
                    <span className={`status-badge ${cat.status?.toLowerCase()}`}>
                      {cat.status || 'Active'}
                    </span>
                  </td>
                  <td>
                    {cat.createdAt
                      ? new Date(cat.createdAt).toLocaleDateString()
                      : '-'}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon-edit"
                        onClick={() => handleEdit(cat)}
                        title="Edit"
                      >
                        <HiOutlinePencil />
                      </button>
                      <button
                        className="btn-icon-delete"
                        onClick={() => setDeleteConfirm(cat)}
                        title="Delete"
                      >
                        <HiOutlineTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create/Edit Modal */}
      <SharedModal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingCategory ? 'Edit Root Cause Category' : 'New Root Cause Category'}
        footer={
          <>
            <button
              type="button"
              className="btn-secondary btn"
              onClick={handleCloseModal}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="root-cause-category-form"
              className="btn-primary btn"
              disabled={submitting}
            >
              {submitting
                ? editingCategory
                  ? 'Updating...'
                  : 'Creating...'
                : editingCategory
                  ? 'Update'
                  : 'Create'}
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <form id="root-cause-category-form" onSubmit={handleSubmit} className="modal-form">
            <div className="form-group">
              <label htmlFor="categoryCode">
                Category Code <span className="required">*</span>
              </label>
              <input
                type="text"
                id="categoryCode"
                value={formData.categoryCode}
                onChange={(e) =>
                  setFormData({ ...formData, categoryCode: e.target.value.toUpperCase() })
                }
                className={formErrors.categoryCode ? 'error' : ''}
                placeholder="e.g., MAN, MACHINE, METHOD"
                maxLength={50}
              />
              {formErrors.categoryCode && (
                <span className="field-error">{formErrors.categoryCode}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="categoryName">
                Category Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="categoryName"
                value={formData.categoryName}
                onChange={(e) =>
                  setFormData({ ...formData, categoryName: e.target.value })
                }
                className={formErrors.categoryName ? 'error' : ''}
                placeholder="e.g., Man (People), Machine (Equipment)"
                maxLength={100}
              />
              {formErrors.categoryName && (
                <span className="field-error">{formErrors.categoryName}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                className={formErrors.description ? 'error' : ''}
                placeholder="Enter description (optional)"
                rows={3}
                maxLength={500}
              />
              {formErrors.description && (
                <span className="field-error">{formErrors.description}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                value={formData.status}
                onChange={(e) =>
                  setFormData({ ...formData, status: e.target.value })
                }
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
          </form>
        </div>
      </SharedModal>

      {/* Delete Confirmation Modal */}
      <SharedModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Confirm Delete"
        customClass="small"
        footer={
          <>
            <button
              type="button"
              className="btn-secondary btn"
              onClick={() => setDeleteConfirm(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-danger btn"
              onClick={() => handleDelete(deleteConfirm.rootCauseCategoryId)}
            >
              Delete
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <p>
            Are you sure you want to delete{' '}
            <strong>{deleteConfirm?.categoryName}</strong>?
          </p>
          <p className="warning-text">
            This action cannot be undone. The category will be marked as inactive.
          </p>
        </div>
      </SharedModal>
    </div>
  );
};

export default RootCauseCategories;

